import { enableTsBilling } from 'config/app'
import routes from './routes'
import createQueryString from 'components/customHooks/useQueryString'
import { UserPermission } from 'components/lab/user/permissions'
import { DEVICE_COMPLAINTS_FILTER_PARAM, DEVICE_COMPLAINTS_MENU_BADGE } from 'constants/deviceComplaints'
import type { Item } from 'components/atoms/SidebarSection'
import type { SavedBucketDto } from 'store/types'
import { getSavedBucketTestTypeLabel, normalizeSavedBucketTestType, SAVED_BUCKET_TEST_TYPES, } from './savedBuckets'

const baseMenuData: Item[] = [
  {
    label: 'Recent Orders',
    icon: 'fas fa-list-alt',
    to: `${routes.index}${routes.order.root}?${createQueryString({
      bucket: 'RECENT',
      testType: 'POX_COX_HST',
    })}&actionItems=1`,
    bucket: 'RECENT',
  },
  {
    label: 'Patient Directory',
    to: `${routes.index}${routes.patient.root}${routes.patient.directory}`,
  },
  {
    to: false,
    label: 'Order Operations',
    icon: 'fas fa-headset',
    open: false,
    linkClass: 'active',
    sub: [
      {
        to: `https://testsmarterinc.freshdesk.com/`,
        label: 'Support Tickets',
        icon: 'fas fa-headset',
        bucket: 'SUPPORT_TICKETS',
        badgeParams: { testType: 'TICKETS', bucket: 'SUPPORT_TICKETS' },
        badgeClass: 'badge-danger',
      },
      {
        to: `${routes.index}${routes.fax.root}${routes.fax.incoming}?bucket=IN_DOC`,
        badgeParams: { testType: 'ALL', bucket: 'FAXES' },
        bucket: 'IN_DOC',
        label: 'Incoming Documents',
        icon: 'fas fa-list-alt',
        badgeClass: 'badge-warning',
      },
      {
        label: 'Failed Faxes',
        icon: 'fas fa-list-alt',
        to: `${routes.index}${routes.order.root}?bucket=FAILED_FAXES&testType=POX,COX`,
        bucket: 'FAILED_FAXES',
        badgeParams: [
          { testType: 'POX_COX', bucket: 'FAILED_FAXES' },
          { testType: 'HST', bucket: 'FAILED_FAXES' },
        ],
        badgeClass: 'badge-warning',
      },
      {
        label: 'Awaiting Review',
        icon: 'fas fa-list-alt',
        to: `${routes.index}${routes.order.root}?${createQueryString(
          routes.order.actionItems.awaitingReview,
        )}&actionItems=1`,
        bucket: routes.order.actionItems.awaitingReview.bucket,
        badgeParams: [routes.order.actionItems.awaitingReview, routes.order.hst.awaitingReview],
        badgeClass: 'badge-warning',
      },
      {
        label: 'Need Attention',
        icon: 'fas fa-list-alt',
        to: `${routes.index}${routes.order.root}?${createQueryString(
          routes.order.actionItems.needAttention,
        )}&actionItems=1`,
        bucket: routes.order.actionItems.needAttention.bucket,
        badgeParams: [routes.order.actionItems.needAttention, routes.order.hst.needAttention],
        badgeClass: 'badge-warning',
      },
      {
        label: 'Demogr. Verification',
        icon: 'fas fa-list-alt',
        to: `${routes.index}${routes.order.root}?${createQueryString(
          routes.order.actionItems.demographicVerification,
        )}&actionItems=1`,
        bucket: routes.order.actionItems.demographicVerification.bucket,
        badgeParams: [
          routes.order.actionItems.demographicVerification,
          routes.order.hst.demographicVerification,
        ],
        badgeClass: 'badge-warning',
      },
      {
        label: 'Insurance Verification',
        icon: 'fas fa-list-alt',
        to: `${routes.index}${routes.order.root}?${createQueryString(
          routes.order.actionItems.insuranceVerification,
        )}&actionItems=1`,
        bucket: routes.order.actionItems.insuranceVerification.bucket,
        badgeParams: [
          routes.order.actionItems.insuranceVerification,
          routes.order.hst.insuranceVerification,
        ],
        badgeClass: 'badge-warning',
      },
      {
        // TES-3191: devices whose probe or patch is due for replacement
        label: 'Accessory Replacement',
        icon: 'fas fa-tools',
        to: `${routes.index}${routes.accessoryReplacement.root}?bucket=DEVICE_ACCESSORIES_DUE`,
        bucket: 'DEVICE_ACCESSORIES_DUE',
        badgeParams: { testType: 'ALL', bucket: 'DEVICE_ACCESSORIES_DUE' },
        badgeClass: 'badge-warning',
      },
      {
        label: 'Messages',
        to: false,
        labelClass: 'text-bold',
      },
      {
        to: `${routes.index}${routes.order.root}?bucket=DM&testType=POX,COX,HST,PSG&actionItems=1`,
        badgeParams: routes.order.actionItems.directMessages,
        bucket: routes.order.actionItems.directMessages.bucket,
        label: 'Direct Messages',
        icon: 'fas fa-list-alt',
        badgeClass: 'badge-warning',
        labelClass: 'ml-2',
      },
      {
        to: `${routes.index}${routes.order.root}?bucket=PATIENT_SMS&testType=POX,COX,HST,PSG&actionItems=1`,
        badgeParams: {
          testType: 'ALL',
          bucket: 'PATIENT_SMS',
        },
        bucket: 'PATIENT_SMS',
        label: 'Patient SMS',
        icon: 'fas fa-mobile-alt',
        badgeClass: 'badge-info',
        labelClass: 'ml-2',
      },
    ],
  },
  {
    to: false,
    label: 'Billing',
    icon: 'fas fa-list',
    open: false,
    linkClass: 'active',
    sub: [
      {
        label: 'Awaiting Authorization',
        to: `${routes.index}${routes.order.root}?${createQueryString(
          routes.order.billing.authorization,
        )}`,
        badgeParams: routes.order.billing.authorization,
        bucket: routes.order.billing.authorization.bucket,
        icon: 'fas fa-list-alt',
        badgeClass: 'badge-warning',
      },
      {
        label: 'Billing Review',
        to: `${routes.index}${routes.order.root}?${createQueryString(
          routes.order.billing.billingReview,
        )}`,
        badgeParams: routes.order.billing.billingReview,
        bucket: routes.order.billing.billingReview.bucket,
        icon: 'fas fa-list-alt',
        badgeClass: 'badge-warning',
      },
      {
        label: 'Hold',
        to: `${routes.index}${routes.order.root}?${createQueryString(
          routes.order.billing.billingHold,
        )}`,
        badgeParams: routes.order.billing.billingHold,
        bucket: routes.order.billing.billingHold.bucket,
        icon: 'fas fa-list-alt',
        badgeClass: 'badge-warning',
      },
      {
        label: 'Pending to CureMD',
        to: `${routes.index}${routes.order.root}?${createQueryString(
          routes.order.billing.billingQueued,
        )}`,
        badgeParams: routes.order.billing.billingQueued,
        bucket: routes.order.billing.billingQueued.bucket,
        icon: 'fas fa-list-alt',
        badgeClass: 'badge-warning',
      },
      {
        label: 'Failed to CureMD',
        to: `${routes.index}${routes.order.root}?${createQueryString(
          routes.order.billing.billingFailed,
        )}`,
        badgeParams: routes.order.billing.billingFailed,
        bucket: routes.order.billing.billingFailed.bucket,
        icon: 'fas fa-list-alt',
        badgeClass: 'badge-danger',
      },
      {
        label: 'Missing Charge Rule',
        to: `${routes.index}${routes.order.root}?${createQueryString(
          routes.order.billing.billingChargeBlocked,
        )}`,
        badgeParams: routes.order.billing.billingChargeBlocked,
        bucket: routes.order.billing.billingChargeBlocked.bucket,
        icon: 'fas fa-list-alt',
        badgeClass: 'badge-danger',
      },
      {
        // Mixed-office orders waiting for the Client Bill vs Insurance call.
        label: 'Client Bill Review',
        to: `${routes.index}${routes.order.root}?${createQueryString(
          routes.order.billing.clientBillReview,
        )}`,
        badgeParams: routes.order.billing.clientBillReview,
        bucket: routes.order.billing.clientBillReview.bucket,
        icon: 'fas fa-list-alt',
        badgeClass: 'badge-warning',
      },
      ...(enableTsBilling
        ? [
            {
              label: 'Charges',
              labelClass: 'text-bold',
              to: false,
            },
            {
              label: 'All Charges',
              to: `${routes.index}${routes.billing.root}${routes.billing.charge}?bucket=${routes.order.billing.billingChargesAll.bucket}`,
              badgeParams: routes.order.billing.billingChargesAll,
              bucket: routes.order.billing.billingChargesAll.bucket,
              labelClass: 'ml-2',
              badgeClass: 'badge-primary',
            },
            {
              label: 'Pending for submission',
              to: `${routes.index}${routes.billing.root}${routes.billing.charge}?bucket=${routes.order.billing.billingChargesPendingSubmission.bucket}`,
              badgeParams: routes.order.billing.billingChargesPendingSubmission,
              bucket: routes.order.billing.billingChargesPendingSubmission.bucket,
              labelClass: 'ml-2',
              badgeClass: 'badge-warning',
            },
            {
              label: 'Submitted',
              to: `${routes.index}${routes.billing.root}${routes.billing.charge}?bucket=${routes.order.billing.billingChargesSubmitted.bucket}`,
              badgeParams: routes.order.billing.billingChargesSubmitted,
              bucket: routes.order.billing.billingChargesSubmitted.bucket,
              labelClass: 'ml-2',
              badgeClass: 'badge-info',
            },
            {
              label: 'Failed to submit',
              to: `${routes.index}${routes.billing.root}${routes.billing.charge}?bucket=${routes.order.billing.billingChargesFailedSubmission.bucket}`,
              badgeParams: routes.order.billing.billingChargesFailedSubmission,
              bucket: routes.order.billing.billingChargesFailedSubmission.bucket,
              labelClass: 'ml-2',
              badgeClass: 'badge-danger',
            },
            {
              label: 'Active',
              to: `${routes.index}${routes.billing.root}${routes.billing.charge}?bucket=${routes.order.billing.billingChargesActive.bucket}`,
              badgeParams: routes.order.billing.billingChargesActive,
              bucket: routes.order.billing.billingChargesActive.bucket,
              labelClass: 'ml-2',
              badgeClass: 'badge-warning',
            },
            {
              label: 'Paid',
              to: `${routes.index}${routes.billing.root}${routes.billing.charge}?bucket=${routes.order.billing.billingChargesPaid.bucket}`,
              badgeParams: routes.order.billing.billingChargesPaid,
              bucket: routes.order.billing.billingChargesPaid.bucket,
              labelClass: 'ml-2',
              badgeClass: 'badge-success',
            },
            {
              // Submitted claims that came back rejected (277 from the Optum
              // mailbox — clearinghouse-level or payer-level, the bucket does
              // not tell them apart).
              label: 'Rejected',
              to: `${routes.index}${routes.billing.root}${routes.billing.charge}?bucket=${routes.order.billing.billingChargesRejected.bucket}`,
              badgeParams: routes.order.billing.billingChargesRejected,
              bucket: routes.order.billing.billingChargesRejected.bucket,
              labelClass: 'ml-2',
              badgeClass: 'badge-danger',
            },
            {
              // Charges a biller flagged to follow up on, until resolved.
              label: 'Needs Work',
              to: `${routes.index}${routes.billing.root}${routes.billing.needsWork}`,
              badgeParams: routes.order.billing.billingNeedsWork,
              bucket: routes.order.billing.billingNeedsWork.bucket,
              badgeClass: 'badge-danger',
            },
            {
              label: 'Outstanding Balances',
              to: `${routes.index}${routes.billing.root}${routes.billing.pendingStatements}`,
              badgeParams: routes.order.billing.pendingStatements,
              bucket: routes.order.billing.pendingStatements.bucket,
              badgeClass: 'badge-warning',
            },
            {
              label: 'Ready to Print',
              to: `${routes.index}${routes.billing.root}${routes.billing.paperStatements}`,
              badgeParams: routes.order.billing.paperStatementsReady,
              bucket: routes.order.billing.paperStatementsReady.bucket,
              badgeClass: 'badge-warning',
            },
            {
              label: 'Denials',
              to: `${routes.index}${routes.billing.root}${routes.billing.denials}`,
              badgeParams: routes.order.billing.billingDenials,
              bucket: routes.order.billing.billingDenials.bucket,
              badgeClass: 'badge-danger',
            },
            {
              label: 'ERA Files',
              to: `${routes.index}${routes.billing.root}${routes.billing.eraFiles}`,
              badgeParams: routes.order.billing.billingEraFiles,
              bucket: routes.order.billing.billingEraFiles.bucket,
              badgeClass: 'badge-warning',
            },
            {
              label: 'ERA Exceptions',
              to: `${routes.index}${routes.billing.root}${routes.billing.eraExceptions}`,
            },
          ]
        : []),
    ],
  },
  {
    label: 'PulseOx-CapOx',
    open: false,
    to: false,
    linkClass: 'active',
    menu: 'pulseox-capox',
    sub: [
      {
        label: 'Active Orders',
        to: `${routes.index}${routes.order.root}?${createQueryString(
          routes.order.pulseOxCapOx.active,
        )}`,
        bucket: routes.order.pulseOxCapOx.active.bucket,
        badgeParams: routes.order.pulseOxCapOx.active,
        badgeClass: 'badge-info',
      },
      {
        label: 'Open Orders',
        to: `${routes.index}${routes.order.root}?${createQueryString(
          routes.order.pulseOxCapOx.open,
        )}`,
        bucket: routes.order.pulseOxCapOx.open.bucket,
        badgeParams: routes.order.pulseOxCapOx.open,
        badgeClass: 'badge-warning',
      },
      {
        label: 'Demogr. Verification',
        to: `${routes.index}${routes.order.root}?${createQueryString(
          routes.order.pulseOxCapOx.demographicVerification,
        )}`,
        bucket: routes.order.pulseOxCapOx.demographicVerification.bucket,
        badgeParams: routes.order.pulseOxCapOx.demographicVerification,
        badgeClass: 'badge-warning',
      },
      {
        label: 'Insurance Verification',
        to: `${routes.index}${routes.order.root}?${createQueryString(
          routes.order.pulseOxCapOx.insuranceVerification,
        )}`,
        bucket: routes.order.pulseOxCapOx.insuranceVerification.bucket,
        badgeParams: routes.order.pulseOxCapOx.insuranceVerification,
        badgeClass: 'badge-warning',
      },
      {
        label: 'Need Attention',
        icon: 'fas fa-list-alt',
        to: `${routes.index}${routes.order.root}?${createQueryString(
          routes.order.pulseOxCapOx.needAttention,
        )}`,
        bucket: routes.order.pulseOxCapOx.needAttention.bucket,
        badgeParams: routes.order.pulseOxCapOx.needAttention,
        badgeClass: 'badge-warning',
      },
      {
        label: 'Awaiting Review',
        to: `${routes.index}${routes.order.root}?${createQueryString(
          routes.order.pulseOxCapOx.awaitingReview,
        )}`,
        bucket: routes.order.pulseOxCapOx.awaitingReview.bucket,
        badgeParams: routes.order.pulseOxCapOx.awaitingReview,
        badgeClass: 'badge-warning',
      },
      {
        label: 'Logistics',
        labelClass: 'text-bold',
      },
      {
        label: 'New/Ready to Schedule',
        to: `${routes.index}${routes.order.root}?${createQueryString(
          routes.order.pulseOxCapOx.readyToSchedule,
        )}`,
        labelClass: 'ml-2',
        bucket: routes.order.pulseOxCapOx.readyToSchedule.bucket,
        badgeParams: routes.order.pulseOxCapOx.readyToSchedule,
        badgeClass: 'badge-warning',
      },
      {
        label: 'Follow up',
        to: `${routes.index}${routes.order.root}?${createQueryString(
          routes.order.pulseOxCapOx.followUp,
        )}`,
        labelClass: 'ml-2',
        bucket: routes.order.pulseOxCapOx.followUp.bucket,
        badgeParams: routes.order.pulseOxCapOx.followUp,
        badgeClass: 'badge-warning',
      },
      {
        label: 'Need to Ship',
        bucket: routes.order.pulseOxCapOx.needToShip.bucket,
        to: `${routes.index}${routes.order.root}?${createQueryString(
          routes.order.pulseOxCapOx.needToShip,
        )}`,
        labelClass: 'ml-2',
        badgeParams: routes.order.pulseOxCapOx.needToShip,
        badgeClass: 'badge-warning',
      },
      {
        label: 'Ready to Ship',
        bucket: routes.order.pulseOxCapOx.readyToShip.bucket,
        to: `${routes.index}${routes.order.root}?${createQueryString(
          routes.order.pulseOxCapOx.readyToShip,
        )}`,
        labelClass: 'ml-2',
        badgeParams: routes.order.pulseOxCapOx.readyToShip,
        badgeClass: 'badge-warning',
      },
      {
        label: 'Shipped to Patient',
        bucket: routes.order.pulseOxCapOx.shipped.bucket,
        to: `${routes.index}${routes.order.root}?${createQueryString(
          routes.order.pulseOxCapOx.shipped,
        )}`,
        labelClass: 'ml-2',
        badgeParams: routes.order.pulseOxCapOx.shipped,
        badgeClass: 'badge-warning',
      },
      {
        label: 'Delivered',
        bucket: routes.order.pulseOxCapOx.delivered.bucket,
        to: `${routes.index}${routes.order.root}?${createQueryString(
          routes.order.pulseOxCapOx.delivered,
        )}`,
        labelClass: 'ml-2',
        badgeParams: routes.order.pulseOxCapOx.delivered,
        badgeClass: 'badge-warning',
      },
      {
        label: 'Pending Return',
        bucket: routes.order.pulseOxCapOx.pendingReturn.bucket,
        to: `${routes.index}${routes.order.root}?${createQueryString(
          routes.order.pulseOxCapOx.pendingReturn,
        )}`,
        labelClass: 'ml-2',
        badgeParams: routes.order.pulseOxCapOx.pendingReturn,
        badgeClass: 'badge-warning',
      },
      {
        label: 'Shipped to TestSmarter',
        bucket: routes.order.pulseOxCapOx.shippedBack.bucket,
        to: `${routes.index}${routes.order.root}?${createQueryString(
          routes.order.pulseOxCapOx.shippedBack,
        )}`,
        labelClass: 'ml-2',
        badgeParams: routes.order.pulseOxCapOx.shippedBack,
        badgeClass: 'badge-warning',
      },
      {
        label: 'Delivered Back',
        bucket: routes.order.pulseOxCapOx.deliveredBack.bucket,
        to: `${routes.index}${routes.order.root}?${createQueryString(
          routes.order.pulseOxCapOx.deliveredBack,
        )}`,
        labelClass: 'ml-2',
        badgeParams: routes.order.pulseOxCapOx.deliveredBack,
        badgeClass: 'badge-warning',
      },
      {
        label: 'Completed',
        bucket: routes.order.pulseOxCapOx.completed.bucket,
        to: `${routes.index}${routes.order.root}?${createQueryString(
          routes.order.pulseOxCapOx.completed,
        )}`,
        badgeParams: routes.order.pulseOxCapOx.completed,
        badgeClass: 'badge-info',
      },
      {
        label: 'Cancelled',
        bucket: routes.order.pulseOxCapOx.cancelled.bucket,
        to: `${routes.index}${routes.order.root}?${createQueryString(
          routes.order.pulseOxCapOx.cancelled,
        )}`,
        badgeParams: routes.order.pulseOxCapOx.cancelled,
        badgeClass: 'badge-danger',
      },
      {
        label: 'All Orders',
        bucket: routes.order.pulseOxCapOx.all.bucket,
        to: `${routes.index}${routes.order.root}?${createQueryString(
          routes.order.pulseOxCapOx.all,
        )}`,
        badgeParams: routes.order.pulseOxCapOx.all,
        badgeClass: 'badge-info',
      },
    ],
  },
  {
    to: false,
    label: 'Home Sleep Testing',
    icon: 'fas fa-hdd',
    open: false,
    linkClass: 'active',
    sub: [
      {
        label: 'Open Orders',
        to: `${routes.index}${routes.order.root}?${createQueryString(routes.order.hst.open)}`,
        bucket: routes.order.hst.open.bucket,
        badgeParams: routes.order.hst.open,
        badgeClass: 'badge-warning',
      },
      {
        label: 'Processing',
        labelClass: 'text-bold',
      },
      {
        label: 'Verification',
        to: `${routes.index}${routes.order.root}?${createQueryString(
          routes.order.hst.orderVerification,
        )}`,
        labelClass: 'ml-2',
        bucket: routes.order.hst.orderVerification.bucket,
        badgeParams: routes.order.hst.orderVerification,
        badgeClass: 'badge-warning',
      },
      {
        label: 'Demogr. Verification',
        to: `${routes.index}${routes.order.root}?${createQueryString(routes.order.hst.demographicVerification)}`,
        labelClass: 'ml-4',
        bucket: routes.order.hst.demographicVerification.bucket,
        badgeParams: routes.order.hst.demographicVerification,
        badgeClass: 'badge-warning',
      },
      {
        label: 'Insurance Verification',
        to: `${routes.index}${routes.order.root}?${createQueryString(routes.order.hst.insuranceVerification)}`,
        labelClass: 'ml-4',
        bucket: routes.order.hst.insuranceVerification.bucket,
        badgeParams: routes.order.hst.insuranceVerification,
        badgeClass: 'badge-warning',
      },
      {
        label: 'Paperwork Verification',
        to: `${routes.index}${routes.order.root}?${createQueryString(routes.order.hst.paperworkVerification)}`,
        labelClass: 'ml-4',
        bucket: routes.order.hst.paperworkVerification.bucket,
        badgeParams: routes.order.hst.paperworkVerification,
        badgeClass: 'badge-warning',
      },
      {
        label: 'Patient Consent',
        to: `${routes.index}${routes.order.root}?${createQueryString(
          routes.order.hst.patientConsent,
        )}`,
        labelClass: 'ml-2',
        bucket: routes.order.hst.patientConsent.bucket,
        badgeParams: routes.order.hst.patientConsent,
        badgeClass: 'badge-warning',
      },
      {
        label: 'Office Notes Verification',
        to: `${routes.index}${routes.order.root}?${createQueryString(routes.order.hst.officeNotesVerification)}`,
        labelClass: 'ml-2',
        bucket: routes.order.hst.officeNotesVerification.bucket,
        badgeParams: routes.order.hst.officeNotesVerification,
        badgeClass: 'badge-warning',
      },
      {
        label: 'Pre Authorization',
        to: `${routes.index}${routes.order.root}?${createQueryString(
          routes.order.hst.preAuthorization,
        )}`,
        labelClass: 'ml-2',
        bucket: routes.order.hst.preAuthorization.bucket,
        badgeParams: routes.order.hst.preAuthorization,
        badgeClass: 'badge-warning',
      },
      {
        label: 'Patient Scheduling',
        to: `${routes.index}${routes.order.root}?${createQueryString(
          routes.order.hst.patientScheduling,
        )}`,
        labelClass: 'ml-2',
        bucket: routes.order.hst.patientScheduling.bucket,
        badgeParams: routes.order.hst.patientScheduling,
        badgeClass: 'badge-warning',
      },
      {
        label: 'Device Shipping',
        to: `${routes.index}${routes.order.root}?${createQueryString(
          routes.order.hst.deviceShipping,
        )}`,
        labelClass: 'ml-2',
        bucket: routes.order.hst.deviceShipping.bucket,
        badgeParams: routes.order.hst.deviceShipping,
        badgeClass: 'badge-warning',
      },
      // {
      //   label: 'Device Delivery',
      //   labelClass: 'text-bold ml-2',
      // },
      // {
      //   label: 'New/Ready to Schedule',
      //   to: `${routes.index}${routes.order.root}?${createQueryString(
      //     routes.order.hst.readyToSchedule,
      //   )}`,
      //   labelClass: 'ml-4',
      //   bucket: routes.order.hst.readyToSchedule.bucket,
      //   badgeParams: routes.order.hst.readyToSchedule,
      //   badgeClass: 'badge-warning',
      // },
      // {
      //   label: 'Follow up',
      //   to: `${routes.index}${routes.order.root}?${createQueryString(routes.order.hst.followUp)}`,
      //   labelClass: 'ml-4',
      //   bucket: routes.order.hst.followUp.bucket,
      //   badgeParams: routes.order.hst.followUp,
      //   badgeClass: 'badge-warning',
      // },
      {
        label: 'Need to Ship',
        bucket: routes.order.hst.needToShip.bucket,
        to: `${routes.index}${routes.order.root}?${createQueryString(routes.order.hst.needToShip)}`,
        labelClass: 'ml-4',
        badgeParams: routes.order.hst.needToShip,
        badgeClass: 'badge-warning',
      },
      {
        label: 'Ready to Ship',
        bucket: routes.order.hst.readyToShip.bucket,
        to: `${routes.index}${routes.order.root}?${createQueryString(
          routes.order.hst.readyToShip,
        )}`,
        labelClass: 'ml-4',
        badgeParams: routes.order.hst.readyToShip,
        badgeClass: 'badge-warning',
      },
      {
        label: 'Shipped to Patient',
        bucket: routes.order.hst.shipped.bucket,
        to: `${routes.index}${routes.order.root}?${createQueryString(routes.order.hst.shipped)}`,
        labelClass: 'ml-4',
        badgeParams: routes.order.hst.shipped,
        badgeClass: 'badge-warning',
      },
      // {
      //   label: 'Delivered',
      //   bucket: routes.order.hst.delivered.bucket,
      //   to: `${routes.index}${routes.order.root}?${createQueryString(routes.order.hst.delivered)}`,
      //   labelClass: 'ml-4',
      //   badgeParams: routes.order.hst.delivered,
      //   badgeClass: 'badge-warning',
      // },
      {
        label: 'Patient Testing',
        to: `${routes.index}${routes.order.root}?${createQueryString(
          routes.order.hst.patientTesting,
        )}`,
        labelClass: 'ml-4',
        bucket: routes.order.hst.patientTesting.bucket,
        badgeParams: routes.order.hst.patientTesting,
        badgeClass: 'badge-warning',
      },
      {
        label: 'Pending Report',
        to: `${routes.index}${routes.order.root}?${createQueryString(
          routes.order.hst.pendingReport,
        )}`,
        labelClass: 'ml-4',
        bucket: routes.order.hst.pendingReport.bucket,
        badgeParams: routes.order.hst.pendingReport,
        badgeClass: 'badge-warning',
      },
      {
        label: 'Needs Review',
        to: `${routes.index}${routes.order.root}?${createQueryString(
          routes.order.hst.wesperReview,
        )}`,
        labelClass: 'ml-4',
        bucket: routes.order.hst.wesperReview.bucket,
        badgeParams: routes.order.hst.wesperReview,
        badgeClass: 'badge-warning',
      },
      {
        label: 'Re-test Pending',
        to: `${routes.index}${routes.order.root}?${createQueryString(
          routes.order.hst.retestPending,
        )}`,
        labelClass: 'ml-4',
        bucket: routes.order.hst.retestPending.bucket,
        badgeParams: routes.order.hst.retestPending,
        badgeClass: 'badge-info',
      },
      {
        label: 'Pending Return',
        bucket: routes.order.hst.pendingReturn.bucket,
        to: `${routes.index}${routes.order.root}?${createQueryString(routes.order.hst.pendingReturn)}`,
        labelClass: 'ml-4',
        badgeParams: routes.order.hst.pendingReturn,
        badgeClass: 'badge-warning',
      },
      {
        label: 'Shipped to TestSmarter',
        bucket: routes.order.hst.shippedBack.bucket,
        to: `${routes.index}${routes.order.root}?${createQueryString(routes.order.hst.shippedBack)}`,
        labelClass: 'ml-4',
        badgeParams: routes.order.hst.shippedBack,
        badgeClass: 'badge-warning',
      },
      {
        label: 'Delivered Back',
        bucket: routes.order.hst.deliveredBack.bucket,
        to: `${routes.index}${routes.order.root}?${createQueryString(routes.order.hst.deliveredBack)}`,
        labelClass: 'ml-4',
        badgeParams: routes.order.hst.deliveredBack,
        badgeClass: 'badge-warning',
      },
      {
        label: 'Completed',
        to: `${routes.index}${routes.order.root}?${createQueryString(routes.order.hst.completed)}`,
        bucket: routes.order.hst.completed.bucket,
        badgeParams: routes.order.hst.completed,
        badgeClass: 'badge-info',
      },
      {
        label: 'Cancelled',
        to: `${routes.index}${routes.order.root}?${createQueryString(routes.order.hst.cancelled)}`,
        bucket: routes.order.hst.cancelled.bucket,
        badgeParams: routes.order.hst.cancelled,
        badgeClass: 'badge-danger',
      },
      {
        label: 'All Orders',
        to: `${routes.index}${routes.order.root}?${createQueryString(routes.order.hst.all)}`,
        bucket: routes.order.hst.all.bucket,
        badgeParams: routes.order.hst.all,
        badgeClass: 'badge-info',
      },
      {
        label: 'Follow-Up Care',
        labelClass: 'text-bold',
        to: false,
      },
      {
        label: 'New',
        to: `${routes.index}${routes.order.root}?${createQueryString(routes.order.hst.followUpCare.new)}`,
        labelClass: 'ml-2',
        bucket: routes.order.hst.followUpCare.new.bucket,
        badgeParams: routes.order.hst.followUpCare.new,
        badgeClass: 'badge-warning',
      },
      {
        label: 'Sent',
        to: `${routes.index}${routes.order.root}?${createQueryString(routes.order.hst.followUpCare.sent)}`,
        labelClass: 'ml-2',
        bucket: routes.order.hst.followUpCare.sent.bucket,
        badgeParams: routes.order.hst.followUpCare.sent,
        badgeClass: 'badge-warning',
      },
      {
        label: 'Follow-up',
        to: `${routes.index}${routes.order.root}?${createQueryString(routes.order.hst.followUpCare.confirmed)}`,
        labelClass: 'ml-2',
        bucket: routes.order.hst.followUpCare.confirmed.bucket,
        badgeParams: routes.order.hst.followUpCare.confirmed,
        badgeClass: 'badge-warning',
      },
    ],
  },
  {
    to: false,
    label: 'In-Lab Sleep Testing',
    icon: 'fas fa-hdd',
    open: false,
    linkClass: 'active',
    sub: [
      {
        label: 'Verification',
        to: `${routes.index}${routes.order.root}?${createQueryString(
          routes.order.psg.orderVerification,
        )}`,
        bucket: routes.order.psg.orderVerification.bucket,
        badgeParams: routes.order.psg.orderVerification,
        badgeClass: 'badge-warning',
      },
      {
        label: 'Patient Consent',
        to: `${routes.index}${routes.order.root}?${createQueryString(
          routes.order.psg.patientConsent,
        )}`,
        bucket: routes.order.psg.patientConsent.bucket,
        badgeParams: routes.order.psg.patientConsent,
        badgeClass: 'badge-warning',
      },
      {
        label: 'Pre Authorization',
        to: `${routes.index}${routes.order.root}?${createQueryString(
          routes.order.psg.preAuthorization,
        )}`,
        bucket: routes.order.psg.preAuthorization.bucket,
        badgeParams: routes.order.psg.preAuthorization,
        badgeClass: 'badge-warning',
      },
      {
        label: 'Patient Scheduling',
        to: `${routes.index}${routes.order.root}?${createQueryString(
          routes.order.psg.patientScheduling,
        )}`,
        bucket: routes.order.psg.patientScheduling.bucket,
        badgeParams: routes.order.psg.patientScheduling,
        badgeClass: 'badge-warning',
      },
      {
        label: 'Patient Scheduled',
        to: `${routes.index}${routes.order.root}?${createQueryString(
          routes.order.psg.patientScheduled,
        )}`,
        bucket: routes.order.psg.patientScheduled.bucket,
        badgeParams: routes.order.psg.patientScheduled,
        badgeClass: 'badge-warning',
      },
      {
        label: 'Completed',
        to: `${routes.index}${routes.order.root}?${createQueryString(routes.order.psg.completed)}`,
        bucket: routes.order.psg.completed.bucket,
        badgeParams: routes.order.psg.completed,
        badgeClass: 'badge-info',
      },
      {
        label: 'Cancelled',
        to: `${routes.index}${routes.order.root}?${createQueryString(routes.order.psg.cancelled)}`,
        bucket: routes.order.psg.cancelled.bucket,
        badgeParams: routes.order.psg.cancelled,
        badgeClass: 'badge-danger',
      },
      {
        label: 'All Orders',
        to: `${routes.index}${routes.order.root}?${createQueryString(routes.order.psg.all)}`,
        bucket: routes.order.psg.all.bucket,
        badgeParams: routes.order.psg.all,
        badgeClass: 'badge-info',
      },
      {
        label: 'Follow-Up Care',
        labelClass: 'text-bold',
        to: false,
      },
      {
        label: 'New',
        to: `${routes.index}${routes.order.root}?${createQueryString(routes.order.psg.followUpCare.new)}`,
        labelClass: 'ml-2',
        bucket: routes.order.psg.followUpCare.new.bucket,
        badgeParams: routes.order.psg.followUpCare.new,
        badgeClass: 'badge-warning',
      },
      {
        label: 'Sent',
        to: `${routes.index}${routes.order.root}?${createQueryString(routes.order.psg.followUpCare.sent)}`,
        labelClass: 'ml-2',
        bucket: routes.order.psg.followUpCare.sent.bucket,
        badgeParams: routes.order.psg.followUpCare.sent,
        badgeClass: 'badge-warning',
      },
      {
        label: 'Follow-up',
        to: `${routes.index}${routes.order.root}?${createQueryString(routes.order.psg.followUpCare.confirmed)}`,
        labelClass: 'ml-2',
        bucket: routes.order.psg.followUpCare.confirmed.bucket,
        badgeParams: routes.order.psg.followUpCare.confirmed,
        badgeClass: 'badge-warning',
      },
    ],
  },
  {
    to: false,
    label: 'Faxes',
    icon: 'fas fa-store',
    open: false,
    linkClass: 'active',
    sub: [
      {
        to: `${routes.index}${routes.fax.root}${routes.fax.incoming}?bucket=IN_DOC`,
        badgeParams: { testType: 'ALL', bucket: 'FAXES' },
        bucket: 'IN_DOC',
        label: 'Incoming Documents',
        icon: 'fas fa-list-alt',
        badgeClass: 'badge-warning',
      },
      {
        to: `${routes.index}${routes.fax.root}${routes.fax.outgoing}?bucket=OUT_DOC`,
        badgeParams: { testType: 'ALL', bucket: 'OUT_DOC' },
        bucket: 'OUT_DOC',
        label: 'Outgoing Documents',
        icon: 'fas fa-list-alt',
        badgeClass: 'badge-warning',
      },
      {
        label: 'Pending',
        icon: 'fas fa-list-alt',
        to: `${routes.index}${routes.order.root}?bucket=PENDING_FAXES&testType=POX,COX,HST,PSG`,
        bucket: 'PENDING_FAXES',
        badgeClass: 'badge-warning',
        badgeParams: [
          { testType: 'POX_COX', bucket: 'PENDING_FAXES' },
          { testType: 'HST', bucket: 'PENDING_FAXES' },
          { testType: 'PSG', bucket: 'PENDING_FAXES' },
        ],
      },
      {
        label: 'Failed Faxes',
        icon: 'fas fa-list-alt',
        to: `${routes.index}${routes.order.root}?bucket=FAILED_FAXES&testType=POX,COX,HST,PSG`,
        bucket: 'FAILED_FAXES',
        badgeParams: [
          { testType: 'POX_COX', bucket: 'FAILED_FAXES' },
          { testType: 'HST', bucket: 'FAILED_FAXES' },
          { testType: 'PSG', bucket: 'FAILED_FAXES' },
        ],
        badgeClass: 'badge-warning',
      },
    ],
  },
  {
    label: 'Device Management',
    icon: 'fas fa-store',
    open: false,
    to: false,
    linkClass: 'active',
    sub: [
      {
        label: 'Device Inventory',
        to: `${routes.index}${routes.device.root}`,
        labelClass: 'ml-2',
      },
      {
        label: 'Device Complaints',
        to: `${routes.index}${routes.device.root}?${DEVICE_COMPLAINTS_FILTER_PARAM}=open`,
        labelClass: 'ml-2',
        bucket: DEVICE_COMPLAINTS_MENU_BADGE.bucket,
        badgeParams: DEVICE_COMPLAINTS_MENU_BADGE,
        badgeClass: 'badge-danger',
      },
    ],
  },
  {
    label: 'Physician Management',
    icon: 'fas fa-store',
    open: false,
    to: `${routes.index}${routes.physician.root}`,
    linkClass: 'active',
  },
  {
    to: false,
    label: 'Patient Tools',
    icon: 'fas fa-store',
    open: false,
    linkClass: 'active',
    sub: [
      // {
      //   label: 'Move Report',
      //   to: `${routes.index}${routes.patient.root}${routes.patient.moveReport}`,
      // },
      // {
      //   label: 'Edit Report Conditions',
      //   to: `${routes.index}${routes.patient.root}${routes.patient.editReportConditions}`,
      // },
      // {
      //   label: 'Merge Patient Accounts',
      //   to: `${routes.index}${routes.patient.root}${routes.patient.mergePatients}`,
      // },
      {
        label: 'Create New Order',
        to: `${routes.index}${routes.order.root}${routes.order.new}`,
      },
    ],
  },
  {
    to: false,
    label: 'Settings',
    icon: 'fas fa-store',
    open: false,
    linkClass: 'active',
    sub: [
      {
        label: 'Lab Insurance Payors',
        to: `${routes.index}${routes.payer.root}`,
      },
      {
        label: 'DME Insurance Payors',
        to: `${routes.index}${routes.dmePayer.root}`,
      },
      {
        label: 'Charge Rules',
        to: `${routes.index}${routes.payerChargeRules.root}`,
      },
      {
        label: 'DME Clients',
        icon: 'fas fa-building',
        to: `${routes.index}${routes.company.root}`,
      },
      {
        label: 'DME Users',
        icon: 'fas fa-people',
        to: `${routes.index}${routes.dmeUsers.root}${routes.dmeUsers.directory}`,
      },
      {
        label: 'Physician Groups',
        icon: 'fas fa-building',
        to: `${routes.index}${routes.physicianGroups.root}`,
      },
      {
        label: 'Physician Group Users',
        icon: 'fas fa-building',
        to: `${routes.index}${routes.physicianGroupUsers.root}${routes.physicianGroupUsers.directory}`,
      },
      // {
      //   label: 'Physicians',
      //   icon: 'fas fa-copyright',
      //   to: `${routes.index}${routes.brand.root}`,
      // },
      // {
      //   label: 'Comply Free Version',
      //   icon: 'fas fa-copyright',
      //   to: `${routes.index}${routes.brand.root}`,
      // },
      {
        label: 'Users Internal',
        icon: 'fas fa-copyright',
        to: `${routes.index}${routes.labUsers.root}${routes.labUsers.directory}`,
      },
      {
        label: 'Bulletins',
        icon: 'fas fa-building',
        to: `${routes.index}${routes.bulletins.root}${routes.bulletins.directory}`,
      },
      {
        label: 'Tags',
        icon: 'fas fa-tags',
        to: `${routes.index}${routes.tags.root}${routes.tags.directory}`,
      },
      {
        label: 'Billing Procedures',
        to: `${routes.index}${routes.billing.root}${routes.billing.procedures}`,
      },
      {
        label: 'Retell Outbound',
        to: `${routes.index}${routes.retellOutboundSettings.root}`,
      },
      {
        label: 'Billing Settings',
        to: `${routes.index}${routes.billingSettings.root}`,
      },
      {
        label: 'Platform Settings',
        to: `${routes.index}${routes.platformSettings.root}`,
      },
    ],
  },
  {
    to: false,
    label: 'Management Reports',
    icon: 'fas fa-store',
    open: false,
    linkClass: 'active',
    sub: [
      {
        to: `${routes.index}${routes.reports.root}${routes.reports.stats}`,
        label: 'Stats',
        icon: 'fas fa-report',
      },
      {
        to: `${routes.index}${routes.reports.root}${routes.reports.patientAging}`,
        label: 'Patient Aging',
        icon: 'fas fa-user-clock',
      },
      {
        to: `${routes.index}${routes.reports.root}${routes.reports.followUpCare}`,
        label: 'Follow-up Care Reports',
        icon: 'fas fa-user-md',
      },
      {
        to: `${routes.index}${routes.reports.root}${routes.reports.preferenceSetup}`,
        label: 'Preference Setup',
        icon: 'fas fa-user-cog',
      },
      {
        to: `${routes.index}${routes.reports.root}${routes.reports.bankDeposit}`,
        label: 'Bank Deposit',
        icon: 'fas fa-money-check-alt',
      },
      {
        to: `${routes.index}${routes.reports.root}${routes.reports.writeOffs}`,
        label: 'Write-Offs',
        icon: 'fas fa-file-invoice-dollar',
      },
      {
        to: `${routes.index}${routes.reports.root}${routes.reports.auditTrail}`,
        label: 'Audit Trail',
        icon: 'fas fa-clipboard-list',
      },
      {
        to: `${routes.index}${routes.retellOutboundActivity.root}`,
        label: 'AI Outbound Calls',
        icon: 'fas fa-phone-volume',
      },
    ],
  },
  {
    to: `${routes.index}${routes.order.diagnostic}`,
    label: 'Order Diagnostics',
    icon: 'fas fa-headset',
    open: false,
    linkClass: 'active',
  },
  {
    label: 'V1 Archive',
    icon: 'fas fa-archive',
    to: `${routes.index}${routes.v1Archive.root}`,
  },
]

const walkMenuItems = (items: Item[]): Item[] =>
  items.flatMap((item) => [item, ...(Array.isArray(item.sub) ? walkMenuItems(item.sub) : [])])

const savedBucketMenuEntryLookup = walkMenuItems(baseMenuData).reduce<Record<string, Item>>(
  (lookup, item) => {
    if (!item.to || typeof item.to !== 'string' || item.to.startsWith('http')) {
      return lookup
    }

    const query = item.to.split('?')[1]
    if (!query) return lookup

    const params = new URLSearchParams(query)
    const bucket = params.get('bucket')
    const testType = normalizeSavedBucketTestType(params.get('testType') ?? undefined)

    if (!bucket || !testType) {
      return lookup
    }

    lookup[`${bucket}:${testType}`] = item
    return lookup
  },
  {},
)

const getMenuEntryBySavedBucket = (savedBucket: SavedBucketDto): Item | undefined =>
  savedBucketMenuEntryLookup[`${savedBucket.bucket}:${savedBucket.testType}`]

const getFallbackSavedBucketLink = (savedBucket: SavedBucketDto): string => {
  const actionItemsBuckets = [
    'DM',
    'PATIENT_SMS',
    'DEMO_VER',
    'INS_VER',
    'NEED_ATTENTION',
    'AWAITING_REVIEW',
  ]
  const query = createQueryString({
    bucket: savedBucket.bucket,
    testType: savedBucket.testType,
  })

  return `${routes.index}${routes.order.root}?${query}${
    actionItemsBuckets.includes(savedBucket.bucket) ? '&actionItems=1' : ''
  }`
}

const buildSavedBucketsMenu = (savedBuckets: SavedBucketDto[]): Item[] => {
  if (!savedBuckets.length) return []

  const groupedSavedBuckets = savedBuckets.reduce<Record<string, SavedBucketDto[]>>(
    (acc, current) => {
      if (!acc[current.testType]) {
        acc[current.testType] = []
      }

      acc[current.testType].push(current)
      return acc
    },
    {},
  )

  return Object.entries(groupedSavedBuckets)
    .sort(
      ([left], [right]) =>
        SAVED_BUCKET_TEST_TYPES.indexOf(left as SavedBucketDto['testType']) -
        SAVED_BUCKET_TEST_TYPES.indexOf(right as SavedBucketDto['testType']),
    )
    .flatMap(([testType, buckets]): Item[] => [
      {
        label: getSavedBucketTestTypeLabel(testType as SavedBucketDto['testType']),
        to: false,
        labelClass: 'text-bold',
      },
      ...buckets
        .slice()
        .sort((left, right) => {
          const leftMenuEntry = getMenuEntryBySavedBucket(left)
          const rightMenuEntry = getMenuEntryBySavedBucket(right)
          const leftLabel = leftMenuEntry?.label || left.bucket
          const rightLabel = rightMenuEntry?.label || right.bucket

          return leftLabel.localeCompare(rightLabel)
        })
        .map((savedBucket): Item => {
          const menuEntry = getMenuEntryBySavedBucket(savedBucket)

          return {
            label: menuEntry?.label || savedBucket.bucket,
            to: menuEntry?.to || getFallbackSavedBucketLink(savedBucket),
            bucket: savedBucket.bucket,
            badgeParams: {
              bucket: savedBucket.bucket,
              testType: savedBucket.testType,
            },
            badgeClass: menuEntry?.badgeClass,
            labelClass: 'ml-2',
          }
        }),
    ])
}

export const getMenuData = (
  savedBuckets: SavedBucketDto[] = [],
  permissions: string[] = [],
): Item[] => {
  const menuData = JSON.parse(JSON.stringify(baseMenuData)) as Item[]

  if (permissions.includes(UserPermission.EDIT_CATALOG)) {
    const physicianManagementIndex = menuData.findIndex(
      (item) => item.label === 'Physician Management',
    )
    const catalogMenuItem: Item = {
      label: 'Catalog Management',
      icon: 'fas fa-boxes',
      open: false,
      to: `${routes.index}${routes.catalog.root}`,
      linkClass: 'active',
    }
    if (physicianManagementIndex === -1) {
      menuData.push(catalogMenuItem)
    } else {
      menuData.splice(physicianManagementIndex + 1, 0, catalogMenuItem)
    }
  }

  if (
    permissions.includes(UserPermission.ADD_READER_APP_VERSION)
    || permissions.includes(UserPermission.REMOVE_READER_APP_VERSION)
  ) {
    const settings = menuData.find((item) => item.label === 'Settings')
    if (settings && Array.isArray(settings.sub)) {
      settings.sub.push({
        label: 'Reader App',
        to: `${routes.index}${routes.readerAppSettings.root}`,
      })
    }
  }

  if (permissions.includes(UserPermission.VIEW_TEAM_PRODUCTIVITY_REPORTS)) {
    const managementReports = menuData.find((item) => item.label === 'Management Reports')
    if (managementReports && Array.isArray(managementReports.sub)) {
      managementReports.sub.push({
        to: `${routes.index}${routes.reports.root}${routes.reports.productivity}`,
        label: 'Productivity Report',
        icon: 'fas fa-chart-line',
      })
    }
  }

  // TES-2981: Analytics (QuickSight embeds, production data) is permission-gated.
  if (permissions.includes(UserPermission.VIEW_ANALYTICS)) {
    const managementReports = menuData.find((item) => item.label === 'Management Reports')
    if (managementReports && Array.isArray(managementReports.sub)) {
      managementReports.sub.push({
        to: `${routes.index}${routes.reports.root}${routes.reports.analytics}`,
        label: 'Analytics',
        icon: 'fas fa-chart-pie',
      })
    }
  }

  // TES-3028: Billing Analytics (QuickSight embed carrying revenue/AR/payer data) is gated by
  // its own permission, independent of the wider operational VIEW_ANALYTICS.
  if (permissions.includes(UserPermission.VIEW_BILLING_ANALYTICS)) {
    const billing = menuData.find((item) => item.label === 'Billing')
    if (billing && Array.isArray(billing.sub)) {
      billing.sub.push({
        to: `${routes.index}${routes.billing.root}${routes.billing.analytics}`,
        label: 'Billing Analytics',
        icon: 'fas fa-chart-pie',
      })
      billing.sub.push({
        to: `${routes.index}${routes.billing.root}${routes.billing.kpiScorecard}`,
        label: 'Billing KPI Scorecard',
        icon: 'fas fa-chart-line',
      })
      billing.sub.push({
        to: `${routes.index}${routes.billing.root}${routes.billing.reports}`,
        label: 'Billing Reports',
        icon: 'fas fa-file-invoice-dollar',
      })
    }
  }

  // TES-2881: gate Bulletins / Tags behind their access permissions.
  const settings = menuData.find((item) => item.label === 'Settings')
  if (settings && Array.isArray(settings.sub)) {
    if (!permissions.includes(UserPermission.ACCESS_BULLETINS)) {
      settings.sub = settings.sub.filter((i) => i.label !== 'Bulletins')
    }
    if (!permissions.includes(UserPermission.MANAGE_TAGS)) {
      settings.sub = settings.sub.filter((i) => i.label !== 'Tags')
    }
    // Charge rules carry contracted rates and drive automated billing; same
    // permission that governs adding/editing lab payers.
    if (!permissions.includes(UserPermission.EDIT_INSURANCE_PAYERS)) {
      settings.sub = settings.sub.filter((i) => i.label !== 'Charge Rules')
    }
    // The statement minimum decides which patients are chased for a balance.
    if (!permissions.includes(UserPermission.MANAGE_BILLING_SETTINGS)) {
      settings.sub = settings.sub.filter((i) => i.label !== 'Billing Settings')
    }
    if (!permissions.includes(UserPermission.MANAGE_PLATFORM_SETTINGS)) {
      settings.sub = settings.sub.filter((i) => i.label !== 'Platform Settings')
    }
  }

  const savedBucketsMenu = buildSavedBucketsMenu(savedBuckets)

  if (!savedBucketsMenu.length) {
    return menuData
  }

  const actionItemsSection: Item = {
    to: false,
    label: 'Action Items',
    icon: 'fas fa-bookmark',
    open: true,
    alwaysOpen: true,
    linkClass: 'active',
    sub: savedBucketsMenu,
  }

  return menuData.flatMap((item) =>
    item.label === 'Patient Directory' ? [item, actionItemsSection] : [item],
  )
}
